/**
 * Customer Control Domain
 * 
 * Bounded area of decision authority for managing the customer journey.
 * Tracks customer value generation, market size, and acquisition channels.
 * 
 * @see docs/KRYSAIS_CONTROL_GLOSSARY.md - "Customer Control Domain" definition
 */

import React, { useEffect, useState } from 'react';
import { Row, Col, Typography, Card, Statistic } from 'antd';
import {
  CustomerServiceOutlined,
  UserOutlined,
  GlobalOutlined,
  FunnelPlotOutlined,
  ShopOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import { useUser } from '../../contexts/UserContext';
import { ValueCreationDetailComponent } from '../../components/Dashboard/ReportingComponents';
import { DashboardSectionCard } from '../../components/Dashboard/DashboardSectionCard';
import {
  isLocalBridgeApiEnabled,
  fetchCustomerSummaryFromLocalBridge,
  type CustomerSummaryFromLocalBridge,
} from '../../services/bridgeSessionDataAdapter';
import styles from './CustomersDashboard.module.css';

const { Title, Text } = Typography;

// ============================================================================
// CUSTOMER CONTROL DOMAIN
// ============================================================================

export const CustomersDashboard: React.FC = () => {
  const { selectedCompany } = useUser();
  const [customerSummary, setCustomerSummary] = useState<CustomerSummaryFromLocalBridge | null>(null);

  useEffect(() => {
    if (!selectedCompany || !isLocalBridgeApiEnabled()) return;
    fetchCustomerSummaryFromLocalBridge(selectedCompany.id).then(setCustomerSummary);
  }, [selectedCompany]);

  const formatStat = (value: number | null | undefined, asPercent?: boolean) => {
    if (value == null) return '—';
    if (asPercent) return `${(value * 100).toFixed(1)}%`;
    return value.toFixed(2);
  };

  return (
    <div className={styles.dashboard}>
      {/* Control Domain Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <CustomerServiceOutlined className={styles.headerIcon} />
          <div>
            <Title level={2} className={styles.title}>Customer Control</Title>
            <Text className={styles.subtitle}>
              Manage customer journey, value generation, and market coordination
            </Text>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.statCard}>
            <Statistic
              title="Active Customers"
              value={formatStat(customerSummary?.active_customers)}
              prefix={<UserOutlined />}
              className={styles.statistic}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.statCard}>
            <Statistic
              title="Value Ratio"
              value={formatStat(customerSummary?.customer_value_ratio)}
              prefix={<GlobalOutlined />}
              className={styles.statistic}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.statCard}>
            <Statistic
              title="Channels"
              value={formatStat(customerSummary?.channels_count)}
              prefix={<FunnelPlotOutlined />}
              className={styles.statistic}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.statCard}>
            <Statistic
              title="Growth Rate"
              value={formatStat(customerSummary?.growth_rate, true)}
              prefix={<RiseOutlined />}
              suffix={customerSummary?.growth_rate != null ? '%' : undefined}
              className={styles.statistic}
            />
          </Card>
        </Col>
      </Row>

      {/* Customer Value Creation */}
      <div className={styles.section}>
        <ValueCreationDetailComponent
          artifactId={selectedCompany?.id || 'customers'}
        />
      </div>

      {/* Navigation Cards */}
      <Row gutter={[24, 24]}>
        <Col xs={24} md={8}>
          <DashboardSectionCard
            title="Market Size Analysis"
            icon={<GlobalOutlined />}
            description="Total addressable market and segmentation"
            metrics={[
              { label: 'TAM', value: '—' },
              { label: 'SAM', value: '—' },
              { label: 'SOM', value: '—' },
            ]}
          />
        </Col>
        <Col xs={24} md={8}>
          <DashboardSectionCard
            title="Marketing Channels"
            icon={<FunnelPlotOutlined />}
            description="Customer acquisition channels and performance"
            metrics={[
              { label: 'Active', value: '—' },
              { label: 'CAC', value: '—' },
              { label: 'Conversion', value: '—' },
            ]}
          />
        </Col>
        <Col xs={24} md={8}>
          <DashboardSectionCard
            title="Customers by Product"
            icon={<ShopOutlined />}
            description="Customer distribution across products"
            metrics={[
              { label: 'Products', value: '—' },
              { label: 'Avg per Product', value: '—' },
            ]}
          />
        </Col>
      </Row>
    </div>
  );
};

export default CustomersDashboard;
